/**
 * Dashboard Parts — presentational helpers extracted from `DashboardContent.js`
 * to keep that file under control. Pure components / constants with no shared
 * state. Imported by `DashboardContent.js` and the in-canvas `renderFullBlock`.
 */
import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Video, Upload, Trash2, Plus, Loader2, EyeOff, Eye, ImageIcon,
  ArrowUp, ArrowDown, Pencil, Palette, X as XIcon, FolderOpen, Layout,
  ChevronsUp, ChevronsDown,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { blockLabels, blockIcons, BLOCK_TYPES } from './blockTypes';
import { getWbButtonAttrs } from '../sqs/WbButton';
import WBImage from '../sqs/imageFrameSystem/WBImage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ─────────────────────────────────────────────────────────────
 *  Fonts, sizes, colors, helpers
 * ─────────────────────────────────────────────────────────── */
export const FONTS = [
  { name: 'System Default',    value: 'inherit' },
  { name: 'Syne',              value: "'Syne', sans-serif" },
  { name: 'Playfair Display',  value: "'Playfair Display', serif" },
  { name: 'Montserrat',        value: "'Montserrat', sans-serif" },
  { name: 'Lora',              value: "'Lora', serif" },
  { name: 'Raleway',           value: "'Raleway', sans-serif" },
  { name: 'Oswald',            value: "'Oswald', sans-serif" },
  { name: 'Merriweather',      value: "'Merriweather', serif" },
  { name: 'Nunito',            value: "'Nunito', sans-serif" },
  { name: 'Poppins',           value: "'Poppins', sans-serif" },
  { name: 'Roboto Slab',       value: "'Roboto Slab', serif" },
  { name: 'Crimson Text',      value: "'Crimson Text', serif" },
  { name: 'Work Sans',         value: "'Work Sans', sans-serif" },
  { name: 'DM Sans',           value: "'DM Sans', sans-serif" },
  { name: 'Space Grotesk',     value: "'Space Grotesk', sans-serif" },
  { name: 'Bitter',            value: "'Bitter', serif" },
];

export const IMAGE_SIZES = [
  { label: 'Small',      value: 'small',  maxW: 'max-w-sm',  maxH: 'max-h-48' },
  { label: 'Medium',     value: 'medium', maxW: 'max-w-lg',  maxH: 'max-h-72' },
  { label: 'Large',      value: 'large',  maxW: 'max-w-3xl', maxH: 'max-h-96' },
  { label: 'Full Width', value: 'full',   maxW: 'max-w-full', maxH: '' },
];

export const COLOR_PRESETS = ['#ffffff', '#f8fafc', '#f1f5f9', '#e2e8f0', '#1e293b', '#0f172a', '#000000', '#7c3aed', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'];

export const autoFontColor = (bgColor, hasImage) => {
  if (hasImage) return '#ffffff';
  if (!bgColor || bgColor === 'transparent') return '#1e293b';
  const hex = bgColor.replace('#', '');
  if (hex.length !== 6) return '#1e293b';
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 150 ? '#1e293b' : '#ffffff';
};

const loadedFonts = new Set();
export const loadFont = (fontFamily) => {
  if (!fontFamily || fontFamily === 'inherit' || loadedFonts.has(fontFamily)) return;
  const name = fontFamily.replace(/'/g, '').split(',')[0].trim();
  if (['inherit', 'sans-serif', 'serif'].includes(name)) return;
  loadedFonts.add(fontFamily);
  const link = document.createElement('link');
  link.href = `https://fonts.googleapis.com/css2?family=${name.replace(/ /g, '+')}:wght@400;600;700&display=swap`;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

/* ─────────────────────────────────────────────────────────────
 *  Block-style controls (slide-out panel section)
 * ─────────────────────────────────────────────────────────── */
export const BlockStyleControls = ({ block, onUpdate }) => {
  const c = block.content || {};
  const style = c._style || {};
  const isFormType = ['form'].includes(block.type);
  const hasText = !['spacer', 'gallery'].includes(block.type);

  const updateStyle = (key, val) => {
    const newStyle = { ...style, [key]: val };
    if (key === 'bgColor' || key === 'bgImage') {
      const hasImg = key === 'bgImage' ? !!val : !!style.bgImage;
      const bg = key === 'bgColor' ? val : style.bgColor;
      if (!style.fontColor || style.fontColor === autoFontColor(style.bgColor, !!style.bgImage)) {
        newStyle.fontColor = autoFontColor(bg, hasImg);
      }
    }
    onUpdate(block.id, '_style', newStyle);
  };

  return (
    <div className="space-y-5 border-t border-white/10 pt-5 mt-4">
      <p className="text-xs font-bold text-[#A78BFA] uppercase tracking-wider flex items-center gap-2"><Palette size={12} /> Block Styling</p>

      {/* Background Color */}
      <div>
        <Label className="text-[#9CA3AF] text-xs mb-2 block">Background Color</Label>
        <div className="flex flex-wrap gap-1.5">
          {COLOR_PRESETS.map(c2 => (
            <button key={c2} onClick={() => updateStyle('bgColor', c2)} className={`w-6 h-6 rounded-lg border-2 transition-all ${style.bgColor === c2 ? 'border-[#8B5CF6] scale-110' : 'border-white/10 hover:border-white/30'}`} style={{ backgroundColor: c2 }} />
          ))}
          <input type="color" value={style.bgColor || '#ffffff'} onChange={(e) => updateStyle('bgColor', e.target.value)} className="w-6 h-6 rounded-lg cursor-pointer border-0" title="Custom color" />
        </div>
      </div>

      {/* Background Image (not for forms) */}
      {!isFormType && (
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-2 block">Background Image</Label>
          {style.bgImage ? (
            <div className="relative h-20 rounded-lg overflow-hidden group">
              <img src={style.bgImage} alt="" className="w-full h-full object-cover" />
              <button onClick={() => updateStyle('bgImage', '')} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100"><XIcon size={10} /></button>
            </div>
          ) : (
            <label className="flex items-center gap-2 p-3 border border-dashed border-white/10 rounded-lg cursor-pointer hover:border-[#8B5CF6]/40 transition-all">
              <ImageIcon size={14} className="text-[#9CA3AF]" /><span className="text-[#9CA3AF] text-xs">Add background image</span>
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files[0]; if (!file) return;
                const fd = new FormData(); fd.append('file', file);
                try { const r = await axios.post(`${API}/media/upload`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' } }); updateStyle('bgImage', r.data.url); } catch { toast.error('Upload failed'); }
              }} />
            </label>
          )}
        </div>
      )}

      {/* Font Family */}
      {hasText && (
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-2 block">Font</Label>
          <select value={style.fontFamily || 'inherit'} onChange={(e) => { loadFont(e.target.value); updateStyle('fontFamily', e.target.value); }} className="w-full bg-white/5 border border-white/10 rounded-lg text-white text-sm p-2" data-testid="font-select">
            {FONTS.map(f => <option key={f.value} value={f.value} style={{ fontFamily: f.value, color: '#000' }}>{f.name}</option>)}
          </select>
        </div>
      )}

      {/* Font Color */}
      {hasText && (
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-2 block">Font Color</Label>
          <div className="flex flex-wrap gap-1.5">
            {['#ffffff', '#f8fafc', '#1e293b', '#000000', '#7c3aed', '#3b82f6', '#10b981', '#ef4444', '#ec4899'].map(c2 => (
              <button key={c2} onClick={() => updateStyle('fontColor', c2)} className={`w-6 h-6 rounded-lg border-2 transition-all ${style.fontColor === c2 ? 'border-[#8B5CF6] scale-110' : 'border-white/10'}`} style={{ backgroundColor: c2 }} />
            ))}
            <input type="color" value={style.fontColor || '#1e293b'} onChange={(e) => updateStyle('fontColor', e.target.value)} className="w-6 h-6 rounded-lg cursor-pointer border-0" />
          </div>
        </div>
      )}

      {/* Image Size (for blocks with images) */}
      {['single_image', 'about', 'custom_image'].includes(block.type) && (
        <div>
          <Label className="text-[#9CA3AF] text-xs mb-2 block">Image Size</Label>
          <div className="flex gap-2">
            {IMAGE_SIZES.map(s => (
              <button key={s.value} onClick={() => updateStyle('imageSize', s.value)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${(style.imageSize || 'large') === s.value ? 'bg-[#8B5CF6] text-white' : 'bg-white/5 text-[#9CA3AF] hover:bg-white/10'}`}>{s.label}</button>
            ))}
          </div>
        </div>
      )}

      {/* Buttons */}
      <div>
        <Label className="text-[#9CA3AF] text-xs mb-2 block">Buttons</Label>
        {(style.buttons || []).map((btn, i) => (
          <div key={i} className="space-y-1.5 mb-3 p-2 bg-white/5 rounded-lg border border-white/5">
            <div className="flex gap-2 items-center">
              <input value={btn.text} onChange={(e) => { const btns = [...(style.buttons || [])]; btns[i] = { ...btns[i], text: e.target.value }; updateStyle('buttons', btns); }} className="flex-1 bg-white/5 border border-white/10 rounded-lg text-white text-xs p-2" placeholder="Button text" />
              <input type="color" value={btn.color || '#8B5CF6'} onChange={(e) => { const btns = [...(style.buttons || [])]; btns[i] = { ...btns[i], color: e.target.value }; updateStyle('buttons', btns); }} className="w-7 h-7 rounded cursor-pointer border-0" title="Button color" />
              <button onClick={() => { const btns = [...(style.buttons || [])]; btns.splice(i, 1); updateStyle('buttons', btns); }} className="text-red-400 hover:text-red-300"><XIcon size={12} /></button>
            </div>
            <div className="flex gap-2 items-center">
              <select value={btn.link_type || 'url'} onChange={(e) => { const btns = [...(style.buttons || [])]; btns[i] = { ...btns[i], link_type: e.target.value, link: e.target.value === 'url' ? (btns[i].link || '') : '' }; updateStyle('buttons', btns); }} className="bg-white/5 border border-white/10 text-white rounded-lg text-[10px] p-1.5 w-20">
                <option value="url">URL</option>
                <option value="page">Page</option>
              </select>
              {(!btn.link_type || btn.link_type === 'url') ? (
                <input value={btn.link || ''} onChange={(e) => { const btns = [...(style.buttons || [])]; btns[i] = { ...btns[i], link: e.target.value }; updateStyle('buttons', btns); }} className="flex-1 bg-white/5 border border-white/10 rounded-lg text-white text-xs p-2" placeholder="https://example.com" data-testid={`btn-link-${i}`} />
              ) : (
                <select value={btn.link || ''} onChange={(e) => { const btns = [...(style.buttons || [])]; btns[i] = { ...btns[i], link: e.target.value }; updateStyle('buttons', btns); }} className="flex-1 bg-white/5 border border-white/10 text-white rounded-lg text-xs p-2" data-testid={`btn-page-${i}`}>
                  <option value="">Select a page...</option>
                  <option value="#home">Home</option>
                  {(window.__wb_pages || []).map(p => <option key={p.id} value={`#page-${p.id}`}>{p.title}</option>)}
                </select>
              )}
            </div>
          </div>
        ))}
        <button onClick={() => updateStyle('buttons', [...(style.buttons || []), { text: 'Button', link: '#', color: '#8B5CF6', link_type: 'url' }])} className="text-xs text-[#A78BFA] hover:text-white flex items-center gap-1"><Plus size={10} /> Add Button</button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  Block-edit context — provides per-block injection of blockId/onEdit/
 *  fieldStyles to a STABLE, module-level `StableInlineE` component.
 *
 *  Iter 160 — the per-render `E = (props) => <InlineText .../>` wrapper
 *  used to be declared inside `renderFullBlock`, giving E a new component
 *  identity on every parent re-render. React responded by unmounting and
 *  remounting the InlineText on every render, which wiped its internal
 *  `useState(isEditing)` and broke click-to-edit. Now `E` resolves to
 *  `StableInlineE` (module-level → stable across renders) which pulls the
 *  block-scoped props from React Context. Each `renderFullBlock` call
 *  wraps the block in `<BlockEditContext.Provider value={...}>`.
 * ─────────────────────────────────────────────────────────── */
export const BlockEditContext = createContext({ blockId: null, onEdit: () => {}, fieldStyles: {} });

export const StableInlineE = (props) => {
  const ctx = useContext(BlockEditContext);
  return (
    <InlineText
      {...props}
      blockId={props.blockId || ctx.blockId}
      onEdit={props.onEdit || ctx.onEdit}
      fieldStyle={props.fieldStyle || (ctx.fieldStyles && ctx.fieldStyles[props.field])}
    />
  );
};

/* ─────────────────────────────────────────────────────────────
 *  Inline editable text — contentEditable wrapper
 *
 *  Iter 154 — now reads `block.content._field_styles[field]` and applies
 *  per-field typography (font, size, weight, color, decoration, alignment,
 *  line-height, letter-spacing, transform, link). Dispatches a
 *  `wb:focus-text` CustomEvent on focus so the global TextToolbar can
 *  anchor above this element.
 * ─────────────────────────────────────────────────────────── */
export const InlineText = ({ value, field, blockId, onEdit, className = '', tag = 'p', placeholder = 'Click to edit...', fieldStyle = null }) => {
  const ref = useRef(null);
  // Iter 160 — contentEditable is deferred to a real click-into-edit so:
  //   1. The browser doesn't start its own native text-selection drag on
  //      mousedown (which was stealing the gesture from useFluidTextDrag
  //      and causing inline-text elements to "snap back" on release).
  //   2. The cursor at rest is `grab` (from .wb-inline-text), telling the
  //      user the element can be moved freely. Only when they have
  //      committed to editing does the cursor become text/I-beam.
  // The mousedown→click handshake works because useFluidTextDrag installs
  // a `swallowClick` listener after a >5px drag: clicks following a real
  // drag are swallowed, so a drag never accidentally enters edit mode.
  const [isEditing, setIsEditing] = useState(false);
  // Uncontrolled mirroring — set DOM content imperatively so React's
  // reconciler never wipes user keystrokes mid-edit. Iter 157 — when the
  // stored value is HTML (contains tags from a rich paste), use innerHTML
  // so the formatting survives a re-render; otherwise fall back to
  // textContent so plain strings can't smuggle markup.
  const isHtml = (v) => typeof v === 'string' && /<[a-z][\s\S]*>/i.test(v);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    const next = value || '';
    const isRich = isHtml(next);
    if (isRich) {
      if (el.innerHTML !== next) el.innerHTML = next;
    } else if (el.textContent !== next) {
      el.textContent = next;
    }
  }, [value]);
  const handleBlur = () => {
    if (!ref.current) {
      setIsEditing(false);
      return;
    }
    // Persist HTML if the user pasted formatted content, otherwise plain
    // text so simple edits don't accumulate noisy markup.
    const html = ref.current.innerHTML;
    const text = ref.current.textContent;
    const next = /<[a-z][\s\S]*>/i.test(html) ? html : text;
    if (next !== value) onEdit(blockId, field, next);
    setIsEditing(false);
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ref.current?.blur(); } };
  const handleFocus = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    window.dispatchEvent(new CustomEvent('wb:focus-text', {
      detail: {
        blockId,
        field,
        rect: { top: r.top, left: r.left, bottom: r.bottom, right: r.right, width: r.width, height: r.height },
      },
    }));
  };
  // When a user really CLICKS (not drags) on the text, enter edit mode.
  // useFluidTextDrag swallows the click if a drag actually happened, so
  // this only fires for genuine taps.
  const enterEditMode = () => {
    setIsEditing(true);
    // Focus + caret-at-end after React applies contentEditable=true.
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      try {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (_) { /* selection API can fail on detached nodes */ }
    });
  };
  const handleClick = (e) => {
    e.stopPropagation();
    if (isEditing) return;
    // Iter 196c — Enter edit mode on ANY click within the text bounds.
    // The previous 25%-75% "drag band" made text feel unresponsive
    // (Squarespace/Wix treat the whole text as the tap target). Drags are
    // still handled by useFluidTextDrag, which swallows the trailing click
    // when a real drag (>5px) actually happened — so drags won't
    // accidentally enter edit mode.
    enterEditMode();
  };
  // Iter 160 — listen for the custom `wb:inline-text-click` event dispatched
  // from useFluidTextDrag's mouseup-without-drag path. This is the reliable
  // way to detect a "tap to edit" because the native click target can be
  // hijacked by the SectionControlCard popping in between mousedown/mouseup.
  useEffect(() => {
    const onCustomClick = (e) => {
      const d = e.detail || {};
      if (d.blockId !== blockId || d.field !== field) return;
      if (isEditing) return;
      enterEditMode();
    };
    window.addEventListener('wb:inline-text-click', onCustomClick);
    return () => window.removeEventListener('wb:inline-text-click', onCustomClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockId, field, isEditing]);
  // Iter 157 — Rich-paste handler. Preserves inline styling on the
  // pasted HTML (font, color, weight, etc.) while stripping unsafe nodes
  // (script/iframe) and document-chrome wrappers (html/head/body). The
  // sanitized fragment is inserted via execCommand so the browser's own
  // contentEditable selection model handles cursor placement.
  const handlePaste = (e) => {
    const dt = e.clipboardData || window.clipboardData;
    if (!dt) return;
    const html = dt.getData('text/html');
    if (!html) return; // let the browser handle plain-text paste
    e.preventDefault();
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Strip dangerous nodes
    tmp.querySelectorAll('script, iframe, object, embed, link, meta, style').forEach(n => n.remove());
    // Strip wrappers we don't want
    tmp.querySelectorAll('html, head, body').forEach(n => { n.replaceWith(...n.childNodes); });
    // Clean event-handler attributes
    tmp.querySelectorAll('*').forEach(n => {
      [...n.attributes].forEach(a => {
        if (/^on/i.test(a.name)) n.removeAttribute(a.name);
        if (a.name === 'class' && /^Mso/i.test(a.value)) n.removeAttribute('class');
      });
    });
    document.execCommand('insertHTML', false, tmp.innerHTML);
  };
  // Iter 161 — split-cursor zones requested by the user.
  //   - Middle vertical band (25%–75% of the element's height) shows
  //     cursor: text/I-beam, signalling "click here to edit".
  //   - Top and bottom bands show cursor: grab, signalling "grab the
  //     edge to drag this element to a new position".
  // This is a JS-driven cursor swap because CSS alone can't differentiate
  // a sub-region of an element. The element's own .wb-inline-text rule
  // sets cursor: grab by default; when the cursor enters the middle band
  // we imperatively flip the inline style to 'text' (and back on exit).
  // Iter 178j — Track the current band in a ref so we only mutate
  // `el.style.cursor` when the user actually crosses the 25%/75% boundary.
  // Previously the `if (el.style.cursor !== wanted)` guard helped, but
  // every mousemove still triggered a getBoundingClientRect() + read of
  // `el.style.cursor` + branch, which compounded with the rest of the
  // mousemove pipeline (useFluidTextDrag) to produce the visible cursor
  // jitter the user reported.
  const lastBandRef = useRef(null);
  const handleMouseMove = (e) => {
    if (isEditing) return; // while editing the browser owns the cursor
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const yRel = (e.clientY - r.top) / Math.max(r.height, 1);
    const inMiddleBand = yRel > 0.25 && yRel < 0.75;
    if (lastBandRef.current === inMiddleBand) return; // no change → skip
    lastBandRef.current = inMiddleBand;
    const wanted = inMiddleBand ? 'text' : 'grab';
    el.style.setProperty('cursor', wanted, 'important');
  };
  const handleMouseLeave = () => {
    const el = ref.current;
    if (!el || isEditing) return;
    lastBandRef.current = null;
    el.style.removeProperty('cursor');
  };
  // Build inline style from the per-field style block.
  const s = fieldStyle || {};
  const isPlaceholder = !value;
  const inlineStyle = {
    ...(s.fontFamily && s.fontFamily !== 'inherit' ? { fontFamily: s.fontFamily } : {}),
    ...(s.fontSize    != null ? { fontSize: `${s.fontSize}px` } : {}),
    ...(s.fontWeight  != null ? { fontWeight: s.fontWeight } : {}),
    ...(s.color               ? { color: s.color } : {}),
    ...(s.italic              ? { fontStyle: 'italic' } : {}),
    ...(s.bold                ? { fontWeight: s.fontWeight && s.fontWeight >= 700 ? s.fontWeight : 700 } : {}),
    ...(s.underline || s.strike
      ? { textDecoration: [s.underline && 'underline', s.strike && 'line-through'].filter(Boolean).join(' ') }
      : {}),
    ...(s.align               ? { textAlign: s.align } : {}),
    ...(s.lineHeight  != null ? { lineHeight: s.lineHeight } : {}),
    ...(s.letterSpacing != null ? { letterSpacing: `${s.letterSpacing}px` } : {}),
    ...(s.transform           ? { textTransform: s.transform } : {}),
    ...(isPlaceholder          ? { color: '#aaa', fontStyle: 'italic' } : {}),
  };
  return React.createElement(tag, {
    ref,
    contentEditable: isEditing,
    suppressContentEditableWarning: true,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onPaste: handlePaste,
    onClick: handleClick,
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
    // Iter 160 — Hover model:
    //   - At rest, contentEditable=false ⇒ browser uses CSS cursor (grab).
    //   - Click ⇒ contentEditable=true + focus ⇒ cursor:text (I-beam).
    //   - Blur ⇒ snap back to contentEditable=false ⇒ cursor:grab again.
    // Iter 181 — drop `transition-colors`. While this looks harmless,
    // any color change on hover (e.g. parent's hover state, autocomplete
    // CSS rules, or React state-churn-triggered className diff) animates
    // through colors, which the eye perceives as a "fade" flicker on
    // body text. The InlineText already has no hover-color rules of its
    // own, so removing the transition has zero visual cost.
    className: `wb-inline-text outline-none rounded px-1 -mx-1 ${className}`,
    'data-placeholder': isPlaceholder ? placeholder : undefined,
    'data-testid': `inline-${blockId}-${field}`,
    'data-wb-field': field,
    'data-wb-text': `${blockId}/${field}`,
    'data-wb-active-edit': isEditing ? '1' : undefined,
    style: inlineStyle,
  });
};

/* ─────────────────────────────────────────────────────────────
 *  Image upload zone (drop / pick / replace)
 * ─────────────────────────────────────────────────────────── */
export const ImageUploadZone = ({ imageUrl, onUpload, onMediaPick, className = '', height = 'h-64', label = 'Add Image', blockId, field = 'image_url', frame }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/media/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      onUpload(res.data.url);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  if (imageUrl) {
    return (
      <div className={`relative group overflow-hidden ${className}`}>
        <WBImage
          src={imageUrl}
          blockId={blockId}
          field={field}
          frame={frame}
          className="w-full h-full"
        />
        {/* Iter 178o — Image-replace overlay. Uses a dedicated CSS class so
            it's hover-driven WITHOUT competing with the always-visible
            section chrome chips, and without the pointer-events-auto trap
            that bit us in Iter 178n. The overlay container stays
            pointer-events:none even when visible — only inner buttons
            opt in via inline pointer-events-auto. */}
        <div className="wb-img-overlay absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none">
          <div className="flex gap-2 pointer-events-auto">
            <button onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} className="px-3 py-2 bg-white/90 text-gray-900 rounded-lg text-sm font-medium hover:bg-white">
              <Upload size={14} className="inline mr-1.5" />Replace
            </button>
            <button onClick={(e) => { e.stopPropagation(); onMediaPick?.(); }} className="px-3 py-2 bg-white/90 text-gray-900 rounded-lg text-sm font-medium hover:bg-white">
              <FolderOpen size={14} className="inline mr-1.5" />Library
            </button>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
      </div>
    );
  }

  return (
    <div className={`${height} wb-image-zone border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all bg-gray-50 hover:bg-blue-50/50 group ${className}`} onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} data-testid="image-upload-zone">
      {uploading ? <Loader2 size={28} className="animate-spin text-blue-500" /> : (
        <>
          <div className="w-14 h-14 rounded-2xl bg-gray-200 group-hover:bg-blue-100 flex items-center justify-center mb-3 transition-all">
            <ImageIcon size={24} className="text-gray-400 group-hover:text-blue-500" />
          </div>
          <p className="text-gray-500 text-sm font-medium">{label}</p>
          <p className="text-gray-400 text-xs mt-1">Click to upload or drag & drop</p>
          <div className="flex gap-2 mt-3">
            <span className="px-3 py-1 bg-gray-200 group-hover:bg-blue-500 group-hover:text-white rounded-full text-xs font-medium text-gray-600 transition-all">Upload</span>
            <span onClick={(e) => { e.stopPropagation(); onMediaPick?.(); }} className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-full text-xs font-medium text-gray-600 transition-all">Media Library</span>
          </div>
        </>
      )}
      <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  Insert-block button (legacy inline menu)
 * ─────────────────────────────────────────────────────────── */
export const AddBlockInsertion = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex items-center justify-center py-2 group" data-testid="add-block-insertion">
      <div className="absolute left-8 right-8 h-px bg-gray-200 group-hover:bg-blue-300 transition-all" />
      <button onClick={() => setOpen(!open)} className="relative z-10 w-8 h-8 rounded-full bg-white border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 flex items-center justify-center transition-all shadow-sm hover:shadow-md" data-testid="insert-block-btn">
        <Plus size={16} className="text-gray-400 group-hover:text-blue-500" />
      </button>
      {open && (
        <div className="absolute z-50 top-12 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 w-[420px] max-h-[360px] overflow-y-auto" data-testid="insert-block-menu">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-1">Add a Section</p>
          <div className="grid grid-cols-3 gap-2">
            {BLOCK_TYPES.map(bt => {
              const Icon = blockIcons[bt.type] || Layout;
              return (
                <button key={bt.type} onClick={() => { onAdd(bt.type); setOpen(false); }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-all text-center group/btn" data-testid={`insert-${bt.type}`}>
                  <div className="w-9 h-9 rounded-xl bg-gray-100 group-hover/btn:bg-blue-100 flex items-center justify-center transition-all">
                    <Icon size={16} className="text-gray-500 group-hover/btn:text-blue-600" />
                  </div>
                  <span className="text-[11px] font-medium text-gray-600 group-hover/btn:text-blue-700 leading-tight">{bt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  Block toolbar — floats above each section on hover
 * ─────────────────────────────────────────────────────────── */
export const BlockToolbar = ({ block, onDelete, onMoveUp, onMoveDown, onToggleVis, onEdit, isFirst, isLast }) => (
  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 bg-white rounded-full shadow-xl border border-gray-200 px-2 py-1 opacity-0 group-hover/block:opacity-100 transition-all" data-testid={`toolbar-${block.id}`}>
    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2">{blockLabels[block.type] || block.type}</span>
    <div className="w-px h-4 bg-gray-200" />
    <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-1.5 hover:bg-blue-50 rounded-lg transition-all" title="Edit details"><Pencil size={13} className="text-gray-500 hover:text-blue-600" /></button>
    <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst} className="p-1.5 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-30" title="Move up"><ArrowUp size={13} className="text-gray-500" /></button>
    <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={isLast} className="p-1.5 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-30" title="Move down"><ArrowDown size={13} className="text-gray-500" /></button>
    <button onClick={(e) => { e.stopPropagation(); onToggleVis(); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition-all" title="Toggle visibility">{block.is_visible !== false ? <Eye size={13} className="text-gray-500" /> : <EyeOff size={13} className="text-gray-400" />}</button>
    <div className="w-px h-4 bg-gray-200" />
    <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 hover:bg-red-50 rounded-lg transition-all" title="Delete"><Trash2 size={13} className="text-gray-400 hover:text-red-500" /></button>
  </div>
);

/* ─────────────────────────────────────────────────────────────
 *  Media picker modal
 * ─────────────────────────────────────────────────────────── */
export const MediaPickerModal = ({ open, onClose, onSelect }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/media/files`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFiles(r.data || []))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white text-gray-900 max-w-2xl max-h-[80vh] overflow-y-auto border-none shadow-2xl">
        <DialogHeader><DialogTitle className="text-lg font-bold">Media Library</DialogTitle></DialogHeader>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-500" size={28} /></div> : (
          <div className="grid grid-cols-4 gap-3 mt-2">
            {files.map(f => (
              <button key={f.id} onClick={() => { onSelect(f.url || `/api/media/files/${f.id}`); onClose(); }} className="aspect-square rounded-xl bg-gray-100 border-2 border-transparent hover:border-blue-500 overflow-hidden transition-all" data-testid={`media-pick-${f.id}`}>
                {f.content_type?.startsWith('image') ? <img src={f.url || `${process.env.REACT_APP_BACKEND_URL}/api/media/files/${f.id}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Video size={24} className="text-gray-400" /></div>}
              </button>
            ))}
            {files.length === 0 && <p className="col-span-4 text-center py-8 text-gray-400">No media uploaded yet. Upload from any image block.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  ExtrasImageFrame — self-contained Squarespace Fluid-Engine image
 *  ▸ Position stored on `content._extras_pos[i] = { x, y }`
 *  ▸ Size stored on     `content._image_dims[i] = { w, h }`
 *  ▸ Z-order on         `content._extras_z['image-i']`
 *  ▸ Drag / resize / delete / z-order all wire through custom mouse
 *    handlers so useFluidTextDrag never gets confused about
 *    wrapper-vs-child position (the previous "stuck after release"
 *    bug — Iter 190g).
 *  ▸ Position + size are clamped to the parent section's inner
 *    rectangle so the image can never exceed the template area.
 * ─────────────────────────────────────────────────────────────*/
export const ExtrasImageFrame = ({ blockId, index, img, content, onEdit }) => {
  const wrapperRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const i = index;
  const pos  = content._extras_pos?.[i] || { x: 0, y: i * 40 };
  const dims = content._image_dims?.[i] || { w: 320, h: 240 };
  const zKey = `image-${i}`;
  const z    = content._extras_z?.[zKey] ?? (10 + i);
  const src  = typeof img === 'string' ? img : (img?.url || '');

  // Read the parent section's inner box so we can clamp position + size
  // to the template area. Padding on the extras overlay is 24px so the
  // inner drag-area is `section.width - 48` × `section.height - 48`.
  const getBounds = () => {
    const el = wrapperRef.current;
    if (!el) return null;
    const section = el.closest('[data-testid^="live-block-"]');
    if (!section) return null;
    const r = section.getBoundingClientRect();
    const overlayPad = 24; // matches p-6 on the extras overlay
    return {
      width:  Math.max(80, r.width  - overlayPad * 2),
      height: Math.max(60, r.height - overlayPad * 2),
    };
  };
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

  // ── Drag the entire frame ──────────────────────────────────
  const startDrag = (e) => {
    // Ignore drags that start on the resize handle / z-order chip / delete
    if (e.target.closest('[data-xf-control]')) return;
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = { x: pos.x, y: pos.y };
    const bounds = getBounds();
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      let nx = startPos.x + (ev.clientX - startX);
      let ny = startPos.y + (ev.clientY - startY);
      if (bounds) {
        nx = clamp(nx, 0, Math.max(0, bounds.width  - dims.w));
        ny = clamp(ny, 0, Math.max(0, bounds.height - dims.h));
      }
      const next = { ...(content._extras_pos || {}) };
      next[i] = { x: Math.round(nx), y: Math.round(ny) };
      onEdit(blockId, '_extras_pos', next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── Bottom-right resize (aspect-free), clamped to section ──
  const startResize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = dims.w;
    const startH = dims.h;
    const bounds = getBounds();
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      let nw = Math.max(80, startW + (ev.clientX - startX));
      let nh = Math.max(60, startH + (ev.clientY - startY));
      if (bounds) {
        nw = Math.min(nw, bounds.width  - pos.x);
        nh = Math.min(nh, bounds.height - pos.y);
      }
      const next = { ...(content._image_dims || {}) };
      next[i] = { w: Math.round(nw), h: Math.round(nh) };
      onEdit(blockId, '_image_dims', next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const bump = (delta) => {
    const next = { ...(content._extras_z || {}) };
    next[zKey] = Math.max(0, (next[zKey] ?? z) + delta);
    onEdit(blockId, '_extras_z', next);
  };
  const remove = () => {
    const arr = [...(content._extras_images || [])];
    arr.splice(i, 1);
    // Also rebase companion maps so indices stay in sync.
    const rebaseMap = (m) => {
      if (!m) return m;
      const out = {};
      Object.keys(m).forEach(k => {
        const n = Number(k);
        if (Number.isNaN(n)) { out[k] = m[k]; return; }
        if (n === i) return;              // drop deleted
        out[n < i ? n : n - 1] = m[k];    // shift later entries down
      });
      return out;
    };
    onEdit(blockId, '_extras_images', arr);
    onEdit(blockId, '_extras_pos',  rebaseMap(content._extras_pos));
    onEdit(blockId, '_image_dims',  rebaseMap(content._image_dims));
  };

  return (
    <div
      ref={wrapperRef}
      className="absolute pointer-events-auto"
      style={{
        left: pos.x, top: pos.y, width: dims.w, height: dims.h, zIndex: z,
        cursor: 'grab', touchAction: 'none',
      }}
      data-wb-fluid-frame="1"
      data-wb-frame-index={i}
      data-wb-frame-field="_extras_images"
      data-testid={`extras-img-frame-${i}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={startDrag}
    >
      {src ? (
        <img
          src={src}
          alt={img?.alt || ''}
          className="w-full h-full object-cover rounded-lg pointer-events-none select-none"
          data-wb-image="1"
          data-wb-image-index={i}
          data-wb-image-field="_extras_images"
          draggable={false}
        />
      ) : (
        <div
          className="w-full h-full rounded-lg bg-gradient-to-br from-[#1E1B4B] to-[#0E0E23] border-2 border-dashed border-[#8B5CF6]/40 flex flex-col items-center justify-center text-white/70 text-[11px] font-medium uppercase tracking-wider gap-2 pointer-events-none"
        >
          <ImageIcon size={22} className="opacity-60" />
          <span>Double-click to add image</span>
        </div>
      )}
      {/* Hover chrome — z-order + delete */}
      {hovered && (
        <div
          data-xf-control="1"
          className="absolute -top-9 right-0 flex items-center gap-0.5 bg-[#0E0E23]/95 border border-white/10 rounded-md p-0.5 shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); bump(+5); }}
            className="w-7 h-7 grid place-items-center text-white/85 hover:text-white hover:bg-white/10 rounded"
            title="Bring forward"
            data-testid={`extras-img-forward-${i}`}
          >
            <ChevronsUp size={13} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); bump(-5); }}
            className="w-7 h-7 grid place-items-center text-white/85 hover:text-white hover:bg-white/10 rounded"
            title="Send backward"
            data-testid={`extras-img-backward-${i}`}
          >
            <ChevronsDown size={13} />
          </button>
          <div className="w-px h-4 bg-white/10 mx-0.5" />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); remove(); }}
            className="w-7 h-7 grid place-items-center text-red-400 hover:text-white hover:bg-red-500/40 rounded"
            title="Delete image"
            data-testid={`extras-img-delete-${i}`}
          >
            <XIcon size={13} />
          </button>
        </div>
      )}
      {/* Corner resize handle */}
      {hovered && (
        <div
          data-xf-control="1"
          className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-[#8B5CF6] border-2 border-white rounded-sm cursor-nwse-resize"
          style={{ transform: 'translate(50%, 50%)' }}
          data-testid={`extras-img-resize-${i}`}
          onMouseDown={startResize}
        />
      )}
    </div>
  );
};


/* ─────────────────────────────────────────────────────────────
 *  Full-block hover wrapper used inside renderFullBlock
 * ─────────────────────────────────────────────────────────── */
export const FullBlock = ({ block, children }) => {
  // Iter 190 — Pull `onEdit` from BlockEditContext so we can wire the extras
  // `_extras_text_blocks` entries through `InlineText` (making them
  // click-to-edit like every other text in the canvas). Without this, the
  // <p> rendered below was neither editable nor delegated (the inline-edit
  // hook explicitly skips `data-testid^="inline-"`), so users saw the text
  // block appear but couldn't change it.
  const ctx = useContext(BlockEditContext);
  const onEdit = ctx?.onEdit || (() => {});
  const c = block?.content || {};
  // Section-level meta controls live on block.content but apply to the OUTER
  // wrapper here, not the renderer body. Keeps every ext/*.js renderer
  // untouched while still allowing per-block height + visual attachment.
  const heightStyle = (() => {
    switch (c.section_height) {
      case 'compact':   return { minHeight: 'auto', paddingTop: '1rem', paddingBottom: '1rem' };
      case 'tall':      return { minHeight: '720px', paddingTop: '4rem', paddingBottom: '4rem' };
      case 'fullbleed': return { minHeight: '100vh', paddingTop: '5rem', paddingBottom: '5rem' };
      default:          return {};
    }
  })();
  const attachedStyle = c.attached ? { marginTop: '-1px' } : {}; // collapses visual seam
  const fluidH = c._fluid_section_height > 0 ? { minHeight: `${c._fluid_section_height}px` } : {};

  // ── Universal "extras" zone ────────────────────────────────────────────
  // Anything the user adds inside a section via the Add Block menu lands
  // here so it's GUARANTEED to render — even when the underlying premium
  // renderer doesn't natively show content.buttons or content.text. This
  // is what makes "add a button inside this section" feel right across
  // every section type (pricing, gallery, image, hero, cta, etc.).
  // NEW (Iter 142): `_extras_text_blocks` is an array of independent text
  // entries created by the inline "Duplicate text" tool. Each renders as
  // its own draggable element.
  const extraText  = c._extras_text || '';
  const extraTextBlocks = Array.isArray(c._extras_text_blocks) ? c._extras_text_blocks : [];
  const extraButtons = Array.isArray(c._extras_buttons) ? c._extras_buttons : [];
  const extraImages = Array.isArray(c._extras_images) ? c._extras_images : [];
  const hasExtras = !!extraText || extraTextBlocks.length > 0 || extraButtons.length > 0 || extraImages.length > 0;

  // Section-level entrance animation (applied via [data-wb-anim] +
  // [data-anim-shown] selectors. The toggling happens in useScrollReveal.)
  const animKind  = (c._style && c._style.animation)       || 'none';
  const animDelay = (c._style && c._style.animationDelay)  || 0;
  const animSpeed = (c._style && c._style.animationSpeed)  || 'normal';

  return (
    <div
      className="relative group/section group/block"
      id={`preview-${block.id}`}
      data-testid={`live-block-${block.id}`}
      data-block-id={block.id}
      data-block-type={block.type}
      data-wb-frames={c._image_frames ? JSON.stringify(c._image_frames) : undefined}
      data-wb-focal={c.focus_point ? `${c.focus_point.x ?? 50} ${c.focus_point.y ?? 50}` : undefined}
      data-section-height={c.section_height || 'normal'}
      data-attached={c.attached ? '1' : '0'}
      data-wb-anim={animKind}
      data-wb-anim-speed={animSpeed}
      /* Iter 181 — Editor never plays entrance animations. Always
         render `data-anim-shown="1"`. Previously, sections with an
         animation (e.g. fade-up) rendered with "0" and useScrollReveal
         JS flipped them to "1" via setAttribute. Any subsequent React
         re-render of the parent (which happens constantly during
         mouse-move / state churn while authoring) would briefly drop
         the attribute back to "0" until the next intersection-observer
         callback re-ran, re-firing the 700ms opacity 0→1 transition.
         Symptom: section content (text, images) fades in and out at
         ~5-10 Hz while the user hovers anywhere on the canvas — exactly
         the flicker the user reported. Fix: always emit "1" in the
         editor. The escape-hatch CSS (.builder-edit-canvas [data-wb-anim])
         was a belt that wasn't enough on its own; this is the suspenders. */
      data-anim-shown="1"
      style={{
        opacity: block.is_visible === false ? 0.4 : 1,
        '--wb-anim-delay': `${animDelay}ms`,
        ...heightStyle,
        ...fluidH,
        ...attachedStyle,
      }}
    >
      {children}
      {hasExtras && (
        <div
          className="absolute inset-0 z-20 pointer-events-none p-6 md:p-10"
          data-testid={`extras-zone-${block.id}`}
          data-wb-extras="1"
        >
          {/* Iter 190e — Squarespace Fluid-Engine-style overlay.
              The extras zone spans the ENTIRE section (`inset-0`) instead of
              being pinned to the bottom. Its wrapper is `pointer-events-none`
              so the section body underneath still receives clicks, but every
              extras child adds `pointer-events-auto` (via `wb-inline-text` /
              `wb-sortable-item`) to remain grabbable + editable.
              Children flow from top-left by default; useFluidTextDrag lets the
              vendor drag them anywhere within the section (registers on any
              element with `data-testid^="inline-…"`, applies persisted
              offsets stored on `content._fluid_grid`). */}
          {extraText && (
            <p
              className="text-base md:text-lg leading-relaxed whitespace-pre-wrap text-current opacity-90 mb-3 pointer-events-auto"
              data-wb-extra-text="1"
              data-testid={`inline-${block.id}-_extras_text`}
              data-wb-field="_extras_text"
            >
              {extraText}
            </p>
          )}
          {extraTextBlocks.map((t, i) => {
            const tagKind = t.kind === 'h2' ? 'h2' : t.kind === 'h3' ? 'h3' : 'p';
            const kindClass = tagKind === 'h2'
              ? 'text-3xl md:text-5xl font-bold leading-tight'
              : tagKind === 'h3'
              ? 'text-2xl md:text-3xl font-semibold leading-snug'
              : 'text-base md:text-lg leading-relaxed';
            return (
              <InlineText
                key={t.id || `xt-${i}`}
                blockId={block.id}
                field={`_extras_text_blocks.${i}.text`}
                value={t.text || ''}
                onEdit={onEdit}
                tag={tagKind}
                placeholder="New text — click to edit"
                className={`${kindClass} whitespace-pre-wrap mb-3 wb-inline-text pointer-events-auto`}
              />
            );
          })}
          {extraImages.length > 0 && extraImages.map((img, i) => (
            <ExtrasImageFrame
              key={`ximg-${i}`}
              blockId={block.id}
              index={i}
              img={img}
              content={c}
              onEdit={onEdit}
            />
          ))}
          {extraButtons.length > 0 && (
            <div className="flex flex-wrap gap-3 justify-center pointer-events-auto">
              {extraButtons.map((b, i) => {
                const attrs = getWbButtonAttrs(i, '_extras_buttons');
                return (
                  <a
                    key={i}
                    href={b.link || '#'}
                    {...attrs}
                    className={`inline-block px-7 py-3 rounded-lg text-sm font-bold tracking-wider uppercase transition-all hover:opacity-90 pointer-events-auto ${attrs.className}`}
                    style={{ background: b.color || '#8B5CF6', color: '#fff' }}
                  >
                    {b.text || 'Button'}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  Marquee renderer
 * ─────────────────────────────────────────────────────────── */
export const SEPARATOR_CHARS = { dot: '•', star: '★', slash: '/', dash: '—', none: '' };
export const MARQUEE_SPEED_SEC = { slow: 50, medium: 30, fast: 15 };
export const MARQUEE_SIZE_CLS = { md: 'text-base', lg: 'text-2xl', xl: 'text-4xl', '2xl': 'text-5xl', '3xl': 'text-6xl md:text-7xl' };
export const MARQUEE_HEIGHT_PY = { small: 'py-3', medium: 'py-6', large: 'py-10' };

export const MarqueeRenderer = ({ content }) => {
  const words = Array.isArray(content?.words) ? content.words.filter(Boolean) : [];
  const display = words.length ? words : ['Indigenous Made', 'Authentic', 'Handcrafted', 'Premium'];
  const sep = SEPARATOR_CHARS[content?.separator ?? 'dot'] ?? '•';
  const speed = MARQUEE_SPEED_SEC[content?.speed ?? 'medium'] ?? 30;
  const direction = content?.direction === 'right' ? 'reverse' : 'normal';
  const sizeCls = MARQUEE_SIZE_CLS[content?.font_size ?? 'lg'] ?? 'text-2xl';
  const heightCls = MARQUEE_HEIGHT_PY[content?.height ?? 'medium'] ?? 'py-6';
  const bg = content?.bg_color || '#0F172A';
  const color = content?.text_color || '#FFFFFF';
  const fontWeight = content?.weight === 'regular' ? 500 : 800;
  const fontStyle = content?.italic ? 'italic' : 'normal';
  const loop = [...display, ...display, ...display, ...display];
  return (
    <div className={`relative overflow-hidden ${heightCls}`} style={{ background: bg }} data-testid="marquee-block">
      <style>{`
        @keyframes wb-marquee-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
      <div
        className={`flex whitespace-nowrap items-center gap-10 ${sizeCls}`}
        style={{
          color,
          fontWeight,
          fontStyle,
          letterSpacing: '0.04em',
          animation: `wb-marquee-scroll ${speed}s linear infinite`,
          animationDirection: direction,
          width: 'max-content',
        }}
      >
        {loop.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-10 px-4">
            {w}
            {sep && <span aria-hidden style={{ opacity: 0.55 }}>{sep}</span>}
          </span>
        ))}
      </div>
    </div>
  );
};
